import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Phone, Clock, ChevronUp, ChevronDown, AlertTriangle, CheckCircle, Truck, AlertCircle, Map, ArrowLeft, Eye } from "lucide-react";
import { useAuth } from "./App";
import FollowupPhotosModal from "./components/FollowupPhotosModal";
import InspectorAnnouncementsModal from "./InspectorAnnouncementsModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE = process.env.REACT_APP_BACKEND_URL;

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const parts = dateStr.includes("/") ? dateStr.split("/") : dateStr.split("-");
    if (parts.length === 3) {
      const [y, m, d] = dateStr.includes("-") ? parts : [parts[2], parts[0], parts[1]];
      return `${m}/${d}/${y}`;
    }
  } catch { /* fallback */ }
  return dateStr;
};

const InspectorMyOrdersPage = ({ Layout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canImpersonate = ["Admin", "Dispatch", "Operations"].includes(user?.role);
  // Angela Feb 2026 (Admin Mirror v2): impersonation is ONLY via URL param
  // ?as_inspector=MA-XXX (opened from Inspector Profile "Open Mirror" buttons).
  // No dropdown inside the inspector screen. Read-only view; zero DB writes on load.
  const queryParams = new URLSearchParams(location.search);
  const impersonateAs = canImpersonate ? (queryParams.get("as_inspector") || "").trim() : "";
  const isMirror = !!impersonateAs;
  const initialView = (queryParams.get("view") === "map" || location.state?.openMap) ? "map" : "orders";
  const [data, setData] = useState({ orders: [], hold_orders: [], total_active: 0, total_hold: 0, total_all: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  // Fix C (Angela 2026-05-11) — default sort = Morning Upload GPX route
  // sequence. Falls back to due_date when no sequence is present (legacy
  // path / Morning Upload not uploaded yet).
  const [sortBy, setSortBy] = useState("route_sequence");
  const [sortDir, setSortDir] = useState("asc");
  const [view, setView] = useState(initialView);
  const [followups, setFollowups] = useState([]);
  const [followupCount, setFollowupCount] = useState(0);
  // Knowledge Acknowledgment modal state
  const [ackModalFu, setAckModalFu] = useState(null);
  const [photosModalFu, setPhotosModalFu] = useState(null);  // Sprint B-3
  const [ackOption, setAckOption] = useState("");
  const [ackComment, setAckComment] = useState("");
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackError, setAckError] = useState("");
  const [ackSuccessMsg, setAckSuccessMsg] = useState("");

  const openAckModal = (fu) => {
    setAckModalFu(fu);
    setAckOption("");
    setAckComment("");
    setAckError("");
    setAckSuccessMsg("");
  };
  const closeAckModal = () => {
    setAckModalFu(null);
    setAckOption("");
    setAckComment("");
    setAckError("");
  };
  const submitAck = async () => {
    if (!ackModalFu) return;
    setAckError("");
    if (!ackOption) { setAckError("Please select one option before continuing."); return; }
    if (!ackComment.trim() || ackComment.trim().length < 5) {
      setAckError("Mandatory comment required (min. 5 characters).");
      return;
    }
    setAckSubmitting(true);
    try {
      const res = await axios.patch(`${API}/inspector/followups/${ackModalFu.id}/acknowledge`, {
        response_option: ackOption,
        comment: ackComment.trim(),
      });
      setAckSuccessMsg(res.data?.message || "Acknowledged.");
      fetchOrders();
    } catch (e) {
      setAckError(e.response?.data?.detail || "Failed to submit acknowledgment");
    } finally {
      setAckSubmitting(false);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const params = canImpersonate && impersonateAs ? { as_inspector: impersonateAs } : {};
      const [ordersRes, fuRes] = await Promise.all([
        axios.get(`${API}/inspector/my-orders`, { params }),
        axios.get(`${API}/inspector/followups`, { params }),
      ]);
      setData(ordersRes.data);
      setFollowups(fuRes.data.followups || []);
      setFollowupCount(fuRes.data.count || 0);
    } catch (e) {
      console.error("Failed to fetch my orders:", e);
    } finally {
      setLoading(false);
    }
  }, [canImpersonate, impersonateAs]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(key); setSortDir("asc"); }
  };

  const filteredOrders = useMemo(() => {
    // Combine active + hold orders into a single list (no Hold tab anymore)
    let result = [...(data.orders || []), ...(data.hold_orders || [])];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(o =>
        (o.order_number || "").toLowerCase().includes(term) ||
        (o.address || "").toLowerCase().includes(term) ||
        (o.city || "").toLowerCase().includes(term) ||
        (o.state || "").toLowerCase().includes(term) ||
        (o.zip_code || "").toLowerCase().includes(term) ||
        (o.cliente || "").toLowerCase().includes(term)
      );
    }
    result.sort((a, b) => {
      let aV, bV;
      if (sortBy === "route_sequence") {
        // Fix C — Morning Upload GPX route sequence. Nulls at end so orders
        // without a sequence (GPX absent or not in waypoints) fall after
        // sequenced ones. Within the no-sequence bucket, secondary sort by
        // due_date ASC for stability.
        aV = a.route_sequence == null ? Number.POSITIVE_INFINITY : Number(a.route_sequence);
        bV = b.route_sequence == null ? Number.POSITIVE_INFINITY : Number(b.route_sequence);
        if (aV === bV) {
          const ad = a.due_date || "", bd = b.due_date || "";
          if (ad === bd) return 0;
          return ad < bd ? -1 : 1;
        }
        return sortDir === "asc" ? (aV < bV ? -1 : 1) : (aV > bV ? -1 : 1);
      } else if (sortBy === "stop_point") {
        // Numeric sort with nulls at the end
        aV = a.stop_point == null ? Number.POSITIVE_INFINITY : Number(a.stop_point);
        bV = b.stop_point == null ? Number.POSITIVE_INFINITY : Number(b.stop_point);
      } else {
        aV = a[sortBy] || ""; bV = b[sortBy] || "";
      }
      if (aV === bV) return 0;
      return sortDir === "asc" ? (aV < bV ? -1 : 1) : (aV > bV ? -1 : 1);
    });
    return result;
  }, [data, searchTerm, sortBy, sortDir]);

  const columns = [
    // Fix C (Angela 2026-05-11) — Morning Upload GPX route sequence.
    { key: "route_sequence", label: "#", sortable: true, className: "w-10" },
    { key: "due_date", label: "Due", sortable: true, className: "w-20" },
    { key: "order_number", label: "Order #", sortable: true, className: "w-24" },
    { key: "order_type", label: "Type", sortable: true, className: "w-14" },
    { key: "address", label: "Address", sortable: true, className: "min-w-[180px]" },
    { key: "city", label: "City", sortable: true, className: "w-24" },
    { key: "zip_code", label: "Zip", sortable: true, className: "w-16" },
    { key: "state", label: "State", sortable: true, className: "w-12" },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]"></div>
        </div>
      </Layout>
    );
  }

  // When map view is active, render ONLY the map (full screen, no parent header/buttons)
  if (view === "map") {
    // Angela Feb 2026 (Admin Mirror v2): when an admin is mirroring the map,
    // do NOT apply the negative margin — we need the ADMIN MIRROR banner to
    // remain visible above the map instead of being pulled behind the
    // navbar.  Normal inspector view keeps the original full-bleed map.
    const wrapperStyle = isMirror
      ? { marginLeft: '-1.25rem', marginRight: '-1.25rem' }
      : { margin: '-1.25rem', marginTop: '-1.25rem' };
    return (
      <Layout>
        <div style={wrapperStyle} data-testid="inspector-my-orders-page">
          {isMirror && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-300" data-testid="admin-mirror-banner-map">
              <Eye className="h-4 w-4 text-amber-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-amber-900">
                  ADMIN MIRROR &middot; My Map &mdash; view-only
                </p>
                <p className="text-[10px] text-amber-700 truncate">
                  Viewing as <span className="font-bold">{impersonateAs}</span>. No route is being modified.
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="px-2.5 py-1 rounded-lg bg-white border border-amber-300 text-[11px] font-bold text-amber-800 hover:bg-amber-100 flex-shrink-0"
                data-testid="admin-mirror-exit-btn-map"
              >
                Exit Mirror
              </button>
            </div>
          )}
          <RouteExecutionView orders={data.orders || []} onBack={() => setView("orders")} navigate={navigate} impersonateAs={impersonateAs} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Angela Feb 2026 — PRD Inspector App / Item 1: blocking modal that
          forces the inspector to read admin announcements before working
          on My Orders. Skipped in Admin Mirror mode (read-only view). */}
      {!isMirror && <InspectorAnnouncementsModal />}
      <div className="space-y-5" data-testid="inspector-my-orders-page">
        {/* Angela Feb 2026 (Admin Mirror v2): read-only banner shown ONLY when
            Admin/Dispatch/Operations opened this page with ?as_inspector=MA-XXX
            from the Inspector Profile. Zero DB writes on load. */}
        {isMirror && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-300 rounded-xl" data-testid="admin-mirror-banner">
            <Eye className="h-4 w-4 text-amber-700 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-900">
                ADMIN MIRROR &middot; My Orders &mdash; view-only
              </p>
              <p className="text-[11px] text-amber-700 truncate">
                Viewing as <span className="font-bold">{impersonateAs}</span>. No order, status, or route is being modified.
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-xs font-bold text-amber-800 hover:bg-amber-100"
              data-testid="admin-mirror-exit-btn"
            >
              Exit Mirror
            </button>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800" data-testid="my-orders-title">My Orders</h1>
            <p className="text-gray-500 text-sm mt-0.5">{data.total_all} total orders assigned {data.impersonating_inspector ? `to ${data.impersonating_inspector}` : 'to you'}</p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchOrders(); }}
            className="px-4 py-2 bg-[#D1A054] text-white text-sm rounded-lg hover:bg-[#B8893E] transition-colors"
            data-testid="my-orders-refresh-btn"
          >
            Refresh
          </button>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3" data-testid="inspector-action-buttons">
          <button
            onClick={() => setView(view === "followups" ? "orders" : "followups")}
            className={`relative flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all ${view === "followups" ? "bg-red-600 text-white ring-2 ring-red-400" : "bg-red-50 text-red-700 border-2 border-red-200 hover:bg-red-100"}`}
            data-testid="followup-btn"
          >
            <AlertCircle className="h-5 w-5" />
            Urgent Follow-Up
            {followupCount > 0 && (
              <span className={`absolute -top-2 -right-2 min-w-[22px] h-[22px] flex items-center justify-center rounded-full text-[11px] font-bold ${view === "followups" ? "bg-white text-red-600" : "bg-red-600 text-white animate-pulse"}`} data-testid="followup-badge">{followupCount}</span>
            )}
          </button>
          <button
            onClick={() => setView("map")}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all bg-[#2F5D8A]/5 text-[#2F5D8A] border-2 border-[#2F5D8A]/20 hover:bg-[#2F5D8A]/10"
            data-testid="mymap-btn"
          >
            <Map className="h-5 w-5" />
            My Map
          </button>
        </div>

        {/* Follow-Up View */}
        {view === "followups" && (
          <div className="space-y-3" data-testid="followup-view">
            <div className="flex items-center gap-2">
              <button onClick={() => setView("orders")} className="p-1 text-gray-400 hover:text-gray-600"><ArrowLeft className="h-4 w-4" /></button>
              <h2 className="text-lg font-bold text-red-700">Urgent Follow-Up</h2>
              <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{followupCount}</span>
            </div>
            <p className="text-[11px] text-gray-400">Only special follow-ups sent by Admin / Office are shown here.</p>
            {followups.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No pending follow-ups. You're all caught up!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {followups.map(fu => (
                  <div key={fu.id} className="bg-white rounded-xl border-l-4 border-red-500 border border-red-100 p-4" data-testid={`followup-card-${fu.order_number}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-bold text-gray-800">{fu.order_number}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{fu.address}{fu.city ? `, ${fu.city}` : ""}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fu.company}</p>
                        {fu.reason && <p className="text-xs text-red-600 font-bold mt-1.5">{fu.reason}</p>}
                      </div>
                      <div className="text-right">
                        <span className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold">URGENT</span>
                        <p className="text-[10px] text-gray-400 mt-1">by {fu.sent_by}</p>
                        {fu.sent_at && <p className="text-[10px] text-gray-400">{fu.sent_at.slice(0, 10)}</p>}
                      </div>
                    </div>
                    {fu.proof_images && fu.proof_images.length > 0 && (
                      <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`followup-proof-${fu.order_number}`}>
                        {fu.proof_images.map((img, idx) => (
                          <div key={idx} className="rounded-lg overflow-hidden border border-gray-200 cursor-pointer" onClick={() => window.open(`${BASE}${img}`, '_blank')}>
                            <img src={`${BASE}${img}`} alt="" className="w-full h-32 object-cover" loading="lazy" />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      {fu.status === "acknowledged" || fu.acknowledged_at ? (
                        <div className="flex-1 flex flex-col gap-1.5">
                          <div className="py-2 bg-green-50 border border-green-300 text-green-700 text-xs font-bold rounded-lg text-center flex flex-col items-center justify-center gap-0.5" data-testid={`acknowledged-${fu.order_number}`}>
                            <div className="flex items-center gap-1.5">
                              <CheckCircle className="h-3.5 w-3.5" /> Acknowledged
                            </div>
                            {fu.workflow_status && (
                              <div className="text-[9px] font-normal text-green-600 uppercase tracking-wider">{fu.workflow_status}</div>
                            )}
                          </div>
                          {/* Sprint B-3 (Angela 2026-05-18): close-with-2-photos */}
                          {fu.status !== "completed" && fu.status !== "pending_review" && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPhotosModalFu(fu); }}
                              className="py-1.5 bg-[#6B8F62] hover:bg-[#5a7a52] text-white text-[11px] font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5"
                              data-testid={`followup-close-with-photos-${fu.order_number}`}
                            >
                              📸 Close with 2 photos
                            </button>
                          )}
                          {fu.status === "pending_review" && (
                            <div className="py-1.5 bg-orange-50 border border-orange-200 text-orange-700 text-[11px] font-bold rounded-lg text-center" data-testid={`followup-pending-review-${fu.order_number}`}>
                              ⏳ Aguarda revisão Admin
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); openAckModal(fu); }}
                          className="flex-1 py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`acknowledge-followup-${fu.order_number}`}
                        >
                          <CheckCircle className="h-3.5 w-3.5" /> KNOWLEDGE
                        </button>
                      )}
                      <button onClick={() => navigate(`/order/${fu.order_id}`)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-colors" data-testid={`open-followup-${fu.order_number}`}>
                        View Order
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders View (default) */}
        {view === "orders" && (<>
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
              data-testid="my-orders-search"
            />
          </div>
        </div>

        {/* Angela 2026-05-16 — REMAKE banner. Shown when any active row
            carries dispatch_return_type=REMAKE so the inspector knows a
            return is waiting before they scroll the table. */}
        {filteredOrders.some(o => (o.dispatch_return_type || "").toUpperCase() === "REMAKE") && (
          <div
            className="flex items-start gap-2 bg-[#C07070]/10 border-2 border-[#C07070]/40 text-[#8B4545] rounded-xl px-4 py-3"
            data-testid="remake-banner"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-left text-sm">
              <p className="font-bold uppercase tracking-wider">
                Returned for revision —{" "}
                <span data-testid="remake-banner-count">
                  {filteredOrders.filter(o => (o.dispatch_return_type || "").toUpperCase() === "REMAKE").length}
                </span>{" "}
                order(s)
              </p>
              <p className="text-[12px] text-[#8B4545]/90 leading-snug">
                Please redo the highlighted order(s) below. Tap any row to open and resubmit.
              </p>
            </div>
          </div>
        )}

        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm" data-testid="my-orders-empty">
            No orders assigned to you
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="my-orders-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className={`px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 ${col.className || ''}`}
                        onClick={() => col.sortable && handleSort(col.key)}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {col.sortable && sortBy === col.key && (
                            sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrders.map(order => {
                    const isRemake = (order.dispatch_return_type || "").toUpperCase() === "REMAKE";
                    return (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/order/${order.id}`)}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        isRemake ? "bg-[#C07070]/5 border-l-4 border-l-[#C07070]" : ""
                      }`}
                      data-testid={`my-order-row-${order.order_number}`}
                    >
                      <td className="px-2 py-2.5 text-[11px] whitespace-nowrap" data-testid={`my-order-seq-${order.order_number}`}>
                        {order.route_sequence != null ? (
                          <span
                            className="inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full bg-[#2F5D8A]/10 text-[#2F5D8A] text-[10px] font-bold"
                            title={`Route stop #${order.route_sequence}`}
                          >#{order.route_sequence}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-[11px] whitespace-nowrap text-gray-700">{formatDateDisplay(order.due_date)}</td>
                      <td className="px-2 py-2.5 text-[11px] font-semibold whitespace-nowrap text-gray-900" data-testid={`my-order-number-${order.order_number}`}>
                        <div className="flex items-center gap-1.5">
                          <span>{order.order_number}</span>
                          {isRemake && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-[#C07070] text-white"
                              title={order.dispatch_return_reason || "Returned for revision"}
                              data-testid={`my-order-remake-badge-${order.order_number}`}
                            >
                              <AlertTriangle className="h-2.5 w-2.5" />
                              REMAKE
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-[11px] whitespace-nowrap text-gray-600">{order.order_type || "-"}</td>
                      <td className="px-2 py-2.5 text-[11px] text-gray-700 min-w-[180px] max-w-[260px] truncate" title={order.address || ""} data-testid={`my-order-address-${order.order_number}`}>{order.address || "-"}</td>
                      <td className="px-2 py-2.5 text-[11px] whitespace-nowrap text-gray-600">{order.city || "-"}</td>
                      <td className="px-2 py-2.5 text-[11px] whitespace-nowrap text-gray-600">{order.zip_code || "-"}</td>
                      <td className="px-2 py-2.5 text-[11px] whitespace-nowrap text-gray-600">{order.state || "-"}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* Knowledge Acknowledgment Modal */}
      {/* Sprint B-3 (Angela 2026-05-18) — 2 photos required to close followup */}
      {photosModalFu && (
        <FollowupPhotosModal
          followup={photosModalFu}
          onClose={() => setPhotosModalFu(null)}
          onCompleted={() => { fetchOrders(); }}
        />
      )}

      {ackModalFu && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" data-testid="knowledge-ack-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
              <div>
                <h2 className="text-base font-bold text-[#1F2937]">Knowledge Acknowledgment</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">Order {ackModalFu.order_number} · {ackModalFu.address}</p>
              </div>
              {!ackSuccessMsg && (
                <button onClick={closeAckModal} className="text-gray-400 hover:text-gray-700 text-xl leading-none" data-testid="knowledge-ack-close">×</button>
              )}
            </div>
            {ackSuccessMsg ? (
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-bold text-sm">Acknowledged</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed" data-testid="knowledge-ack-success-message">
                  {ackSuccessMsg}
                </p>
                <button onClick={closeAckModal} className="w-full py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-sm font-bold rounded-lg" data-testid="knowledge-ack-done-btn">
                  Got it
                </button>
              </div>
            ) : (
              <div className="p-5 space-y-4 overflow-y-auto">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                  This order requires correction / follow-up.<br />
                  Please confirm if you believe you should redo this order, and explain what happened.<br />
                  <strong>Your comment is required before continuing.</strong>
                </div>
                {ackModalFu.reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                    <p className="text-[10px] uppercase font-bold text-red-600 tracking-wider mb-0.5">Reason from Office</p>
                    <p className="text-xs text-red-700">{ackModalFu.reason}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold text-gray-700 mb-2">Your response *</p>
                  <div className="space-y-2">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${ackOption === "redo" ? "border-[#2F5D8A] bg-[#2F5D8A]/5" : "border-gray-200 hover:bg-gray-50"}`}>
                      <input
                        type="radio"
                        name="ack-option"
                        value="redo"
                        checked={ackOption === "redo"}
                        onChange={() => setAckOption("redo")}
                        data-testid="knowledge-ack-option-redo"
                        className="h-4 w-4 text-[#2F5D8A]"
                      />
                      <span className="text-sm text-gray-800">I prefer to redo this order</span>
                    </label>
                    <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${ackOption === "reassign" ? "border-[#2F5D8A] bg-[#2F5D8A]/5" : "border-gray-200 hover:bg-gray-50"}`}>
                      <input
                        type="radio"
                        name="ack-option"
                        value="reassign"
                        checked={ackOption === "reassign"}
                        onChange={() => setAckOption("reassign")}
                        data-testid="knowledge-ack-option-reassign"
                        className="h-4 w-4 text-[#2F5D8A]"
                      />
                      <span className="text-sm text-gray-800">Reassign to another inspector</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Comment (required) *</label>
                  <textarea
                    value={ackComment}
                    onChange={e => setAckComment(e.target.value)}
                    placeholder="Explain what happened..."
                    rows={4}
                    maxLength={1000}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#2F5D8A] focus:outline-none resize-none"
                    data-testid="knowledge-ack-comment"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">{ackComment.length}/1000 chars · min. 5 required</p>
                </div>

                {ackError && (
                  <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex items-center gap-1.5" data-testid="knowledge-ack-error">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {ackError}
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={closeAckModal}
                    disabled={ackSubmitting}
                    className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg disabled:opacity-50"
                    data-testid="knowledge-ack-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitAck}
                    disabled={ackSubmitting || !ackOption || ackComment.trim().length < 5}
                    className="flex-1 py-2 bg-[#2F5D8A] hover:bg-[#24496e] disabled:bg-gray-300 text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5"
                    data-testid="knowledge-ack-submit-btn"
                  >
                    {ackSubmitting ? "Submitting..." : "Submit Acknowledgment"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

/* ========================================================================
   RouteExecutionView — Multi-stop route execution
   Launches full route (up to 9 waypoints) in Google Maps native app.
   Manages segments for routes with 10+ stops.
   ======================================================================== */

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/.test(navigator.userAgent);
const MAX_GMAPS_WAYPOINTS = 8; // Google Maps supports destination + 8 intermediate waypoints

// Build multi-stop Google Maps URL for a segment of stops
const buildMultiStopGmapsUrl = (stopsSlice) => {
  if (!stopsSlice || stopsSlice.length === 0) return null;
  const dest = stopsSlice[stopsSlice.length - 1];
  const waypoints = stopsSlice.slice(0, -1);
  let url = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lon}&travelmode=driving`;
  if (waypoints.length > 0) {
    const wpStr = waypoints.map(w => `${w.lat},${w.lon}`).join('|');
    url += `&waypoints=${encodeURIComponent(wpStr)}`;
  }
  return url;
};

// Launch full route segment in native Google Maps app
const launchRouteSegment = (stopsSlice) => {
  if (!stopsSlice || stopsSlice.length === 0) return;

  if (stopsSlice.length === 1) {
    // Single stop — simple navigation
    const s = stopsSlice[0];
    if (isIOS()) {
      const t = Date.now();
      window.location.href = `comgooglemaps://?daddr=${s.lat},${s.lon}&directionsmode=driving`;
      setTimeout(() => { if (Date.now() - t < 1500) window.location.href = `http://maps.apple.com/?daddr=${s.lat},${s.lon}&dirflg=d`; }, 800);
    } else if (isAndroid()) {
      window.location.href = `google.navigation:q=${s.lat},${s.lon}&mode=d`;
    } else {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}&travelmode=driving`, '_blank');
    }
    return;
  }

  // Multi-stop route
  const dest = stopsSlice[stopsSlice.length - 1];
  const waypoints = stopsSlice.slice(0, -1);

  if (isIOS()) {
    // iOS Google Maps app: use comgooglemaps:// with waypoints
    // Format: comgooglemaps://?daddr=LAT1,LNG1+to:LAT2,LNG2+to:...&directionsmode=driving
    const waypointStr = stopsSlice.map(s => `${s.lat},${s.lon}`).join('+to:');
    const gmapsDeepLink = `comgooglemaps://?daddr=${waypointStr}&directionsmode=driving`;
    const appleMapsUrl = `http://maps.apple.com/?daddr=${dest.lat},${dest.lon}&dirflg=d`;

    const t = Date.now();
    window.location.href = gmapsDeepLink;
    setTimeout(() => {
      if (Date.now() - t < 1500) window.location.href = appleMapsUrl;
    }, 800);
  } else if (isAndroid()) {
    // Android: google.navigation only supports single destination
    // Use Google Maps URL intent for multi-stop
    const url = buildMultiStopGmapsUrl(stopsSlice);
    if (url) {
      // Intent URL opens in Google Maps app on Android
      window.location.href = `intent://maps.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lon}&waypoints=${encodeURIComponent(waypoints.map(w => `${w.lat},${w.lon}`).join('|'))}&travelmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`;
    }
  } else {
    const url = buildMultiStopGmapsUrl(stopsSlice);
    if (url) window.open(url, '_blank');
  }
};

// Navigate to a single stop (for "Navigate to This Stop" button)
const launchSingleNav = (stop) => {
  if (!stop) return;
  if (isIOS()) {
    const t = Date.now();
    window.location.href = `comgooglemaps://?daddr=${stop.lat},${stop.lon}&directionsmode=driving`;
    setTimeout(() => { if (Date.now() - t < 1500) window.location.href = `http://maps.apple.com/?daddr=${stop.lat},${stop.lon}&dirflg=d`; }, 800);
  } else if (isAndroid()) {
    window.location.href = `google.navigation:q=${stop.lat},${stop.lon}&mode=d`;
  } else {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lon}&travelmode=driving`, '_blank');
  }
};

const RouteExecutionView = ({ orders, onBack, navigate, impersonateAs }) => {
  const [routeStatus, setRouteStatus] = React.useState("loading");
  const [stops, setStops] = React.useState([]);
  const [currentStop, setCurrentStop] = React.useState(-1);
  const [routeStarted, setRouteStarted] = React.useState(false);
  const [arrivedCurrent, setArrivedCurrent] = React.useState(false);
  const [currentSegment, setCurrentSegment] = React.useState(0); // which segment is active
  const [routeSummary, setRouteSummary] = React.useState(null); // { miles, minutes, skipped }
  const [routingStatus, setRoutingStatus] = React.useState("idle"); // idle|loading|ok|failed
  const [autoMode, setAutoMode] = React.useState(true); // InRoute-style auto-arrival
  const [geoStatus, setGeoStatus] = React.useState("idle"); // idle|requesting|active|denied|unavailable
  const [userCoords, setUserCoords] = React.useState(null); // {lat, lng, accuracy}
  const [distanceToStop, setDistanceToStop] = React.useState(null); // meters
  const mapRef = React.useRef(null);
  const mapInstance = React.useRef(null);
  const markersRef = React.useRef([]);
  const stopsRef = React.useRef([]);
  const routeIdRef = React.useRef(null);
  const progressLineRef = React.useRef(null);
  const drivingLineRef = React.useRef(null); // real OSRM polyline
  const geoWatchRef = React.useRef(null); // navigator.geolocation watch id
  const userMarkerRef = React.useRef(null); // leaflet marker for user's position
  const withinRadiusSinceRef = React.useRef(null); // timestamp when inspector entered arrival radius
  const arrivalHandledForStopRef = React.useRef(-1); // which stop has already auto-arrived
  const currentStopRef = React.useRef(-1);
  const arrivedRef = React.useRef(false);
  const autoModeRef = React.useRef(true);

  React.useEffect(() => { stopsRef.current = stops; }, [stops]);
  React.useEffect(() => { currentStopRef.current = currentStop; }, [currentStop]);
  React.useEffect(() => { arrivedRef.current = arrivedCurrent; }, [arrivedCurrent]);
  React.useEffect(() => { autoModeRef.current = autoMode; }, [autoMode]);

  // SessionStorage
  const saveProgress = (started, stopIdx, advance) => {
    const rId = routeIdRef.current; if (!rId) return;
    try { sessionStorage.setItem(`route_exec_${rId}`, JSON.stringify({ routeStarted: started, currentStopIndex: stopIdx, advanceOnReturn: !!advance })); } catch {}
  };
  const clearProgress = () => { const rId = routeIdRef.current; if (rId) try { sessionStorage.removeItem(`route_exec_${rId}`); } catch {} };

  // Match waypoint to order
  const matchWaypoint = React.useCallback((name, lat, lon) => {
    if (!orders.length) return null;
    if (name) {
      const byNum = orders.find(o => o.order_number && o.order_number === name.trim());
      if (byNum) return byNum;
      const byPart = orders.find(o => o.order_number && name.includes(o.order_number));
      if (byPart) return byPart;
      const nl = name.toLowerCase().replace(/[^a-z0-9 ]/g, '');
      const byAddr = orders.find(o => { const a = (o.address || '').toLowerCase().replace(/[^a-z0-9 ]/g, ''); return a && a.length > 5 && nl.includes(a.slice(0, 15)); });
      if (byAddr) return byAddr;
    }
    if (lat && lon) {
      let closest = null, minD = Infinity;
      orders.forEach(o => { if (o.latitude && o.longitude) { const d = Math.sqrt((lat - parseFloat(o.latitude)) ** 2 + (lon - parseFloat(o.longitude)) ** 2); if (d < minD) { minD = d; closest = o; } } });
      if (closest && minD < 0.002) return closest;
    }
    return null;
  }, [orders]);

  // Build marker icon
  const buildIcon = (idx, total, isCurrent, isDone, isMatched) => {
    const L = window.L; if (!L) return null;
    const isStart = idx === 0, isEnd = idx === total - 1;
    let bg, label, size, bw, shadow;
    if (isCurrent) { bg = '#D1A054'; size = 44; bw = 4; label = isStart ? 'S' : isEnd ? 'E' : `${idx + 1}`; shadow = '0 0 20px rgba(209,160,84,0.8)'; }
    else if (isDone) { bg = '#6B8F62'; size = 24; bw = 2; label = '\u2713'; shadow = '0 1px 3px rgba(0,0,0,0.2)'; }
    else if (isStart) { bg = '#16A34A'; size = 32; bw = 3; label = 'S'; shadow = '0 2px 8px rgba(22,163,74,0.5)'; }
    else if (isEnd) { bg = '#DC2626'; size = 32; bw = 3; label = 'E'; shadow = '0 2px 8px rgba(220,38,38,0.5)'; }
    else { bg = isMatched ? '#2F5D8A' : '#9CA3AF'; size = 24; bw = 2; label = `${idx + 1}`; shadow = '0 1px 3px rgba(0,0,0,0.2)'; }
    const fs = isCurrent ? 16 : (isStart || isEnd) ? 12 : 10;
    return L.divIcon({ className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2],
      html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:${fs}px;border:${bw}px solid #fff;box-shadow:${shadow};cursor:pointer;z-index:${isCurrent ? 999 : isDone ? 1 : 10}">${label}</div>` });
  };

  // Draw done progress line
  const drawProgressLine = React.useCallback((cur) => {
    const L = window.L; if (!L || !mapInstance.current) return;
    if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; }
    const ss = stopsRef.current;
    if (cur > 0 && ss.length > 0) { const coords = ss.slice(0, cur + 1).map(s => [s.lat, s.lon]); if (coords.length > 1) progressLineRef.current = L.polyline(coords, { color: "#6B8F62", weight: 5, opacity: 0.8 }).addTo(mapInstance.current); }
  }, []);

  // Fetch real driving route from OSRM public demo server (free, no key).
  // Returns { geometry: [[lat,lon],...], miles, minutes, skipped } or null on failure.
  const fetchOsrmRoute = React.useCallback(async (pts) => {
    const valid = [];
    const invalidIdx = [];
    pts.forEach((p, i) => {
      const lat = Number(p.lat), lon = Number(p.lon);
      if (isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) valid.push({ lat, lon });
      else invalidIdx.push(i);
    });
    if (valid.length < 2) return { geometry: [], miles: 0, minutes: 0, skipped: invalidIdx.length, ok: false };
    const coordStr = valid.map(p => `${p.lon.toFixed(6)},${p.lat.toFixed(6)}`).join(";");
    const url = `https://router.project-osrm.org/route/v1/driving/${coordStr}?overview=full&geometries=geojson&annotations=false&steps=false`;
    try {
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) throw new Error(`OSRM HTTP ${res.status}`);
      const data = await res.json();
      if (data.code !== "Ok" || !Array.isArray(data.routes) || !data.routes.length) throw new Error("OSRM no route");
      const r0 = data.routes[0];
      const geometry = (r0.geometry?.coordinates || []).map(([lng, lat]) => [lat, lng]);
      return {
        geometry,
        miles: (r0.distance || 0) / 1609.344,
        minutes: (r0.duration || 0) / 60,
        skipped: invalidIdx.length,
        ok: true,
      };
    } catch (e) {
      console.error("OSRM routing failed:", e);
      return { geometry: [], miles: 0, minutes: 0, skipped: invalidIdx.length, ok: false };
    }
  }, []);

  // Draw real driving polyline on the map, respecting current order of stops.
  const drawDrivingRoute = React.useCallback(async (pts) => {
    const L = window.L; if (!L || !mapInstance.current) return;
    setRoutingStatus("loading");
    const result = await fetchOsrmRoute(pts);
    if (!mapInstance.current) return; // unmounted
    if (drivingLineRef.current) { drivingLineRef.current.remove(); drivingLineRef.current = null; }
    if (result && result.ok && result.geometry.length > 1) {
      drivingLineRef.current = L.polyline(result.geometry, { color: "#2F5D8A", weight: 5, opacity: 0.85, lineCap: "round", lineJoin: "round" }).addTo(mapInstance.current);
      setRouteSummary({ miles: result.miles, minutes: result.minutes, skipped: result.skipped, totalStops: pts.length });
      setRoutingStatus("ok");
    } else {
      setRouteSummary({ miles: 0, minutes: 0, skipped: result?.skipped || 0, totalStops: pts.length });
      setRoutingStatus("failed");
    }
  }, [fetchOsrmRoute]);

  // Refresh markers
  const refreshMarkers = React.useCallback((newCur) => {
    const L = window.L; if (!L || !mapInstance.current) return;
    const ss = stopsRef.current, total = ss.length;
    markersRef.current.forEach((m, i) => { const ic = buildIcon(i, total, i === newCur, newCur >= 0 && i < newCur, !!ss[i]?.order); if (ic) m.setIcon(ic); m.setZIndexOffset(i === newCur ? 1000 : i < newCur ? 0 : 500); });
    drawProgressLine(newCur);
  }, [drawProgressLine]);

  // ── Geolocation auto-arrival (InRoute-style) ────────────────────────────
  const ARRIVAL_RADIUS_M = 80; // meters — auto-arrive when inspector is this close
  const ARRIVAL_DWELL_MS = 4000; // must stay within radius for 4s before triggering
  const AUTO_ADVANCE_DELAY_MS = 2500; // pause after arrival before moving to next stop

  const haversineM = (a, b) => {
    const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const updateUserMarker = React.useCallback((lat, lng) => {
    const L = window.L; if (!L || !mapInstance.current) return;
    if (!userMarkerRef.current) {
      const icon = L.divIcon({ className: "", iconSize: [20, 20], iconAnchor: [10, 10],
        html: `<div style="width:20px;height:20px;border-radius:50%;background:#4285F4;border:3px solid #fff;box-shadow:0 0 0 2px rgba(66,133,244,0.35),0 2px 8px rgba(0,0,0,0.3);"></div>` });
      userMarkerRef.current = L.marker([lat, lng], { icon, zIndexOffset: 2000, interactive: false }).addTo(mapInstance.current);
    } else {
      userMarkerRef.current.setLatLng([lat, lng]);
    }
  }, []);

  // Execute the auto-arrival flow for the current stop (called by dwell timer OR
  // when a new GPS reading confirms the inspector has remained within radius long enough).
  const triggerAutoArrival = React.useCallback(() => {
    const ssNow = stopsRef.current;
    const csNow = currentStopRef.current;
    if (csNow < 0 || csNow >= ssNow.length) return;
    if (arrivalHandledForStopRef.current === csNow) return;
    if (!autoModeRef.current) return;
    if (arrivedRef.current) { arrivalHandledForStopRef.current = csNow; return; }
    arrivalHandledForStopRef.current = csNow;
    withinRadiusSinceRef.current = null;
    setArrivedCurrent(true);
    try { if ("vibrate" in navigator) navigator.vibrate([100, 50, 100]); } catch {}
    setTimeout(() => {
      const ss2 = stopsRef.current;
      const cs2 = currentStopRef.current;
      if (cs2 + 1 < ss2.length) {
        const nextIdx = cs2 + 1;
        setCurrentStop(nextIdx); setArrivedCurrent(false); setCurrentSegment(getCurrentSegmentForStop(nextIdx));
        refreshMarkers(nextIdx); saveProgress(true, nextIdx, false);
        if (mapInstance.current && ss2[nextIdx]) mapInstance.current.setView([ss2[nextIdx].lat, ss2[nextIdx].lon], 14, { animate: true });
      } else {
        setRouteStarted(false); setCurrentStop(-1); setArrivedCurrent(false); setCurrentSegment(0);
        refreshMarkers(-1);
        if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; }
        clearProgress();
        if (mapInstance.current && stopsRef.current.length > 0) mapInstance.current.fitBounds(stopsRef.current.map(s => [s.lat, s.lon]), { padding: [40, 40], animate: true });
      }
    }, AUTO_ADVANCE_DELAY_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMarkers]);

  const handleGeoPosition = React.useCallback((pos) => {
    const { latitude, longitude, accuracy } = pos.coords;
    setUserCoords({ lat: latitude, lng: longitude, accuracy });
    updateUserMarker(latitude, longitude);

    // Check arrival condition
    const ss = stopsRef.current;
    const cs = currentStopRef.current;
    if (cs < 0 || cs >= ss.length) return;
    const cur = ss[cs];
    if (!cur || typeof cur.lat !== "number" || typeof cur.lon !== "number") return;
    const d = haversineM({ lat: latitude, lng: longitude }, { lat: cur.lat, lng: cur.lon });
    setDistanceToStop(d);

    if (arrivalHandledForStopRef.current === cs) return;
    if (arrivedRef.current) { arrivalHandledForStopRef.current = cs; return; }
    if (!autoModeRef.current) return;

    if (d <= ARRIVAL_RADIUS_M) {
      const now = Date.now();
      if (withinRadiusSinceRef.current == null) {
        withinRadiusSinceRef.current = now;
        // Arm a dwell timer — if we don't leave radius before it fires, trigger arrival.
        // This prevents us from depending solely on subsequent GPS events (important when
        // the inspector is parked and the device reports few updates).
        setTimeout(() => {
          if (withinRadiusSinceRef.current == null) return; // user moved away before dwell elapsed
          if (arrivalHandledForStopRef.current === currentStopRef.current) return;
          triggerAutoArrival();
        }, ARRIVAL_DWELL_MS + 100);
      } else if (now - withinRadiusSinceRef.current >= ARRIVAL_DWELL_MS) {
        triggerAutoArrival();
      }
    } else {
      withinRadiusSinceRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserMarker, triggerAutoArrival]);

  const handleGeoError = React.useCallback((err) => {
    console.warn("Geolocation error:", err);
    if (err?.code === 1) setGeoStatus("denied");
    else setGeoStatus("unavailable");
  }, []);

  const startGeoWatch = React.useCallback(() => {
    if (!("geolocation" in navigator)) { setGeoStatus("unavailable"); return; }
    if (geoWatchRef.current != null) return; // already watching
    setGeoStatus("requesting");
    try {
      const id = navigator.geolocation.watchPosition(
        (pos) => { setGeoStatus("active"); handleGeoPosition(pos); },
        handleGeoError,
        { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 }
      );
      geoWatchRef.current = id;
    } catch (e) { setGeoStatus("unavailable"); }
  }, [handleGeoPosition, handleGeoError]);

  const stopGeoWatch = React.useCallback(() => {
    if (geoWatchRef.current != null && "geolocation" in navigator) {
      try { navigator.geolocation.clearWatch(geoWatchRef.current); } catch {}
      geoWatchRef.current = null;
    }
    if (userMarkerRef.current && mapInstance.current) {
      try { userMarkerRef.current.remove(); } catch {}
      userMarkerRef.current = null;
    }
    withinRadiusSinceRef.current = null;
    arrivalHandledForStopRef.current = -1;
    setUserCoords(null);
    setDistanceToStop(null);
    setGeoStatus("idle");
  }, []);

  // Stop geolocation on unmount
  React.useEffect(() => () => { stopGeoWatch(); }, [stopGeoWatch]);

  // Reset arrival tracker when inspector manually moves to a different stop
  React.useEffect(() => {
    withinRadiusSinceRef.current = null;
    // do NOT reset arrivalHandledForStopRef here — it's already stop-indexed
  }, [currentStop]);

  // Init map + fetch GPX
  React.useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const L = window.L; if (!L) return;
    const map = L.map(mapRef.current, { zoomControl: false }).setView([42.3601, -71.0589], 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OSM" }).addTo(map);

    // Angela 2026-05-20 — Defensive fallback shared by 3 failure paths
    // (no_route / fetch_failed / empty_gpx). Invoked whenever the primary
    // pin-rendering path produced zero markers on the map.
    let defensiveFallbackInvoked = false;
    const triggerDefensiveFallback = () => {
      if (defensiveFallbackInvoked) return;
      defensiveFallbackInvoked = true;
      axios.get(`${API}/inspector/my-map-pins`, { params: impersonateAs ? { inspector: impersonateAs } : {} })
        .then(r => {
          const pins = r.data?.pins || [];
          const fbBounds = [];
          pins.forEach(p => {
            const lat = parseFloat(p.latitude), lng = parseFloat(p.longitude);
            if (isNaN(lat) || isNaN(lng)) return;
            const isApprox = p.coord_source && !p.coord_source.startsWith("order_geo");
            const color = isApprox ? "#9A7A3E" : "#D1A054";
            const icon = L.divIcon({
              className: '',
              html: `<div data-testid="pin-${p.order_number}" style="width:24px;height:24px;border-radius:50%;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:10px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.2);cursor:pointer">${isApprox ? '~' : '&#9679;'}</div>`,
              iconSize: [24, 24], iconAnchor: [12, 12],
            });
            const m = L.marker([lat, lng], { icon }).addTo(map);
            m.bindTooltip(`<b>${p.order_number}</b><br/>${p.address || ""} ${p.city || ""}${isApprox ? `<br/><i>approx. ${p.coord_source}</i>` : ""}`, { direction: "top" });
            m.on("click", () => navigate(`/order/${p.id}?from=map`));
            fbBounds.push([lat, lng]);
          });
          if (fbBounds.length > 0) map.fitBounds(fbBounds, { padding: [40, 40] });
          if (pins.length > 0) console.info(`[InspectorMap] defensive fallback rendered ${pins.length} pins`, r.data?.by_source);
        })
        .catch(e => console.warn("[InspectorMap] my-map-pins fallback failed:", e));
    };
    L.control.zoom({ position: 'topright' }).addTo(map);
    mapInstance.current = map;
    const allBounds = [];

    axios.get(`${API}/routes/my-route`, { params: impersonateAs ? { as_inspector: impersonateAs } : {} }).then(res => {
      const route = res.data?.route;
      if (route && route.gpx_url) {
        setRouteStatus("found"); routeIdRef.current = route.id;
        fetch(`${BASE}${route.gpx_url}`).then(r => r.text()).then(gpxText => {
          try {
            const parser = new DOMParser(); const gpxDoc = parser.parseFromString(gpxText, "text/xml");
            const linePoints = [];
            gpxDoc.querySelectorAll("trkpt").forEach(pt => { const la = parseFloat(pt.getAttribute("lat")), lo = parseFloat(pt.getAttribute("lon")); if (!isNaN(la) && !isNaN(lo)) linePoints.push([la, lo]); });
            gpxDoc.querySelectorAll("rtept").forEach(pt => { const la = parseFloat(pt.getAttribute("lat")), lo = parseFloat(pt.getAttribute("lon")); if (!isNaN(la) && !isNaN(lo)) linePoints.push([la, lo]); });
            if (linePoints.length > 1) L.polyline(linePoints, { color: "#94A3B8", weight: 2, opacity: 0.25, dashArray: "4, 8" }).addTo(map);
            linePoints.forEach(p => allBounds.push(p));
            const wpts = gpxDoc.querySelectorAll("wpt"); const routeStops = [];
            wpts.forEach(wpt => { const lat = parseFloat(wpt.getAttribute("lat")), lon = parseFloat(wpt.getAttribute("lon")); const name = wpt.querySelector("name")?.textContent || `Stop ${routeStops.length + 1}`; if (!isNaN(lat) && !isNaN(lon)) routeStops.push({ lat, lon, name, order: matchWaypoint(name, lat, lon) }); });

            let restoredStarted = false, restoredStop = -1;
            try { const raw = sessionStorage.getItem(`route_exec_${route.id}`); if (raw) { const sv = JSON.parse(raw); if (sv?.routeStarted) { restoredStarted = true; restoredStop = sv.currentStopIndex; if (sv.advanceOnReturn && restoredStop + 1 < routeStops.length) restoredStop++; sessionStorage.setItem(`route_exec_${route.id}`, JSON.stringify({ routeStarted: true, currentStopIndex: restoredStop, advanceOnReturn: false })); } } } catch {}

            const markers = [], total = routeStops.length;
            routeStops.forEach((stop, idx) => {
              const isCur = restoredStarted && idx === restoredStop; const isDone = restoredStarted && restoredStop >= 0 && idx < restoredStop;
              const icon = buildIcon(idx, total, isCur, isDone, !!stop.order);
              const marker = L.marker([stop.lat, stop.lon], { icon: icon || undefined }).addTo(map);
              marker.bindTooltip(`<b>${idx === 0 ? 'START' : idx === total - 1 ? 'END' : `#${idx + 1}`}</b> ${stop.order ? stop.order.order_number : stop.name}`, { direction: "top", offset: [0, -20] });
              const si = idx;
              marker.on("click", () => {
                setCurrentStop(si); setRouteStarted(true);
                const ss = stopsRef.current;
                markersRef.current.forEach((mk, mi) => { const ic = buildIcon(mi, ss.length, mi === si, si >= 0 && mi < si, !!ss[mi]?.order); if (ic) mk.setIcon(ic); mk.setZIndexOffset(mi === si ? 1000 : mi < si ? 0 : 500); });
                if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; }
                if (si > 0) { const dc = ss.slice(0, si + 1).map(s => [s.lat, s.lon]); if (dc.length > 1) progressLineRef.current = L.polyline(dc, { color: "#6B8F62", weight: 5, opacity: 0.8 }).addTo(mapInstance.current); }
                const rId = routeIdRef.current;
                if (rId) try { sessionStorage.setItem(`route_exec_${rId}`, JSON.stringify({ routeStarted: true, currentStopIndex: si, advanceOnReturn: false })); } catch {}
                mapInstance.current.setView([ss[si].lat, ss[si].lon], 14, { animate: true });
              });
              markers.push(marker); allBounds.push([stop.lat, stop.lon]);
            });
            stopsRef.current = routeStops; markersRef.current = markers; setStops(routeStops);
            // Compute real driving route via OSRM (public demo, no key) on initial load
            if (routeStops.length >= 2) { drawDrivingRoute(routeStops); }
            if (restoredStarted) {
              setRouteStarted(true); setCurrentStop(restoredStop);
              if (restoredStop > 0) { const dc = routeStops.slice(0, restoredStop + 1).map(s => [s.lat, s.lon]); if (dc.length > 1) progressLineRef.current = L.polyline(dc, { color: "#6B8F62", weight: 5, opacity: 0.8 }).addTo(map); }
              setTimeout(() => { if (restoredStop >= 0 && restoredStop < routeStops.length) map.setView([routeStops[restoredStop].lat, routeStops[restoredStop].lon], 14, { animate: true }); }, 500);
            } else { if (allBounds.length > 0) map.fitBounds(allBounds, { padding: [40, 40] }); }
            // Fix F1 (Angela 2026-05-11) — discriminate empty GPX vs valid.
            if (routeStops.length === 0 && linePoints.length === 0) {
              setRouteStatus("empty_gpx");
              triggerDefensiveFallback();
            }
          } catch (e) {
            console.error("GPX parse error:", e);
            setRouteStatus("fetch_failed");
            triggerDefensiveFallback();
          }
        }).catch((e) => {
          console.error("GPX fetch failed:", e);
          setRouteStatus("fetch_failed");
          triggerDefensiveFallback();
        });
      } else {
        setRouteStatus("none");
        orders.forEach(o => {
          if (o.latitude && o.longitude) {
            const lat = parseFloat(o.latitude), lng = parseFloat(o.longitude); if (isNaN(lat) || isNaN(lng)) return;
            const icon = L.divIcon({ className: '', html: `<div style="width:24px;height:24px;border-radius:50%;background:#D1A054;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:10px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.2);cursor:pointer">&#9679;</div>`, iconSize: [24, 24], iconAnchor: [12, 12] });
            const marker = L.marker([lat, lng], { icon }).addTo(map);
            marker.bindTooltip(`<b>${o.order_number}</b><br/>${o.address || ""}`, { direction: "top" });
            marker.on("click", () => navigate(`/order/${o.id}?from=map`));
            allBounds.push([lat, lng]);
          }
        });
        if (allBounds.length === 0) {
          // Angela 2026-05-20 — primary path produced 0 pins; activate defensive fallback.
          triggerDefensiveFallback();
        } else {
          map.fitBounds(allBounds, { padding: [40, 40] });
        }
      }
    }).catch(() => setRouteStatus("none"));
    return () => { map.remove(); mapInstance.current = null; };
  }, [orders, navigate, matchWaypoint, drawDrivingRoute, impersonateAs]);

  // --- Segment logic ---
  const getSegments = () => {
    const ss = stopsRef.current;
    if (ss.length <= MAX_GMAPS_WAYPOINTS + 1) return [{ start: 0, end: ss.length - 1 }];
    const segs = [];
    for (let i = 0; i < ss.length; i += MAX_GMAPS_WAYPOINTS + 1) {
      segs.push({ start: i, end: Math.min(i + MAX_GMAPS_WAYPOINTS, ss.length - 1) });
    }
    return segs;
  };
  const getCurrentSegmentForStop = (stopIdx) => {
    const segs = getSegments();
    for (let i = 0; i < segs.length; i++) { if (stopIdx >= segs[i].start && stopIdx <= segs[i].end) return i; }
    return 0;
  };

  // Route controls
  const startRoute = () => {
    setRouteStarted(true); setCurrentStop(0); setArrivedCurrent(false); setCurrentSegment(0);
    arrivalHandledForStopRef.current = -1; withinRadiusSinceRef.current = null;
    refreshMarkers(0); saveProgress(true, 0, false);
    const ss = stopsRef.current;
    if (ss[0] && mapInstance.current) mapInstance.current.setView([ss[0].lat, ss[0].lon], 14, { animate: true });
    startGeoWatch(); // begin tracking inspector position for auto-arrival
  };

  // Launch full route segment from current stop
  const launchFullRoute = () => {
    const ss = stopsRef.current;
    const segs = getSegments();
    const segIdx = getCurrentSegmentForStop(currentStop);
    setCurrentSegment(segIdx);
    const seg = segs[segIdx];
    const remaining = ss.slice(currentStop, seg.end + 1);
    launchRouteSegment(remaining);
  };

  // Launch navigation to single current stop
  const launchNav = () => launchSingleNav(stopsRef.current[currentStop]);

  const openOrder = () => { if (currentStop >= 0 && stopsRef.current[currentStop]?.order) { saveProgress(true, currentStop, true); navigate(`/order/${stopsRef.current[currentStop].order.id}?from=map`); } };
  const goToStop = (idx) => { if (idx < 0 || idx >= stopsRef.current.length) return; setCurrentStop(idx); setArrivedCurrent(false); setCurrentSegment(getCurrentSegmentForStop(idx)); refreshMarkers(idx); saveProgress(true, idx, false); const ss = stopsRef.current; if (mapInstance.current) mapInstance.current.setView([ss[idx].lat, ss[idx].lon], 14, { animate: true }); };
  const endRoute = () => { setRouteStarted(false); setCurrentStop(-1); setArrivedCurrent(false); setCurrentSegment(0); refreshMarkers(-1); if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; } clearProgress(); stopGeoWatch(); if (mapInstance.current && stopsRef.current.length > 0) mapInstance.current.fitBounds(stopsRef.current.map(s => [s.lat, s.lon]), { padding: [40, 40], animate: true }); };

  const markArrived = () => { setArrivedCurrent(true); };
  const advanceToNext = () => {
    if (currentStop + 1 < stopsRef.current.length) goToStop(currentStop + 1);
    else endRoute();
  };

  const curData = currentStop >= 0 && currentStop < stops.length ? stops[currentStop] : null;
  const hasNext = currentStop + 1 < stops.length;
  const nextData = hasNext ? stops[currentStop + 1] : null;
  const displayName = curData?.order ? curData.order.order_number : (curData?.name || '');
  const displayAddr = curData?.order ? [curData.order.address, curData.order.city, curData.order.state].filter(Boolean).join(', ') : (curData?.name || '');
  const nextName = nextData?.order ? nextData.order.order_number : (nextData?.name || '');
  const nextAddr = nextData?.order ? [nextData.order.address, nextData.order.city].filter(Boolean).join(', ') : '';
  const segments = stops.length > 0 ? getSegments() : [];
  const isEndOfSegment = segments.length > 1 && currentStop === segments[currentSegment]?.end;
  const hasNextSegment = currentSegment + 1 < segments.length;
  const remainingInSegment = segments[currentSegment] ? segments[currentSegment].end - currentStop : 0;

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 56px)' }} data-testid="mymap-view">
      <div className="flex-shrink-0">
        {routeStarted && curData ? (
          <>
            {/* Current stop header */}
            <div className={`text-white px-3 py-2.5 flex items-center gap-3 ${arrivedCurrent ? 'bg-[#16A34A]' : 'bg-[#1a1a2e]'}`} data-testid="nav-header">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-base flex-shrink-0 ${arrivedCurrent ? 'bg-white text-[#16A34A]' : 'bg-[#D1A054] text-white'}`} data-testid="current-stop-number">
                {arrivedCurrent ? '\u2713' : currentStop + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {currentStop === 0 && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-green-500/20 text-green-400">START</span>}
                  {currentStop === stops.length - 1 && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-red-500/20 text-red-400">END</span>}
                  <span className="text-sm font-bold truncate" data-testid="current-stop-label">{displayName}</span>
                </div>
                <p className="text-[11px] text-gray-400 truncate">{displayAddr}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-[#D1A054]" data-testid="stop-progress-text">{currentStop + 1}/{stops.length}</p>
                {segments.length > 1 && <p className="text-[8px] text-gray-500">Seg {currentSegment + 1}/{segments.length}</p>}
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-1.5 bg-gray-200"><div className="h-full bg-gradient-to-r from-[#6B8F62] to-[#8CB584] transition-all duration-500" data-testid="route-progress-bar" style={{ width: `${((currentStop + 1) / stops.length) * 100}%` }} /></div>

            {/* Auto-mode + GPS status */}
            <div className="bg-[#0F1628] px-3 py-1.5 flex items-center justify-between text-[10px] text-gray-300 border-b border-gray-800" data-testid="auto-mode-bar">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 ${geoStatus === "active" ? "text-green-400" : geoStatus === "requesting" ? "text-amber-400" : geoStatus === "denied" ? "text-red-400" : "text-gray-500"}`} data-testid="geo-status">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${geoStatus === "active" ? "bg-green-400 animate-pulse" : geoStatus === "requesting" ? "bg-amber-400 animate-pulse" : geoStatus === "denied" ? "bg-red-400" : "bg-gray-500"}`}></span>
                  {geoStatus === "active" ? "GPS" : geoStatus === "requesting" ? "GPS…" : geoStatus === "denied" ? "GPS denied" : geoStatus === "unavailable" ? "No GPS" : "GPS off"}
                </span>
                {distanceToStop != null && geoStatus === "active" && (
                  <span className="text-gray-400" data-testid="distance-to-stop">
                    · {distanceToStop < 1000 ? `${Math.round(distanceToStop)} m` : `${(distanceToStop / 1609.344).toFixed(1)} mi`} to stop
                  </span>
                )}
              </div>
              <button
                onClick={() => setAutoMode(v => !v)}
                className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${autoMode ? "bg-[#34A853] text-white" : "bg-gray-600 text-gray-200"}`}
                data-testid="auto-mode-toggle"
              >
                AUTO {autoMode ? "ON" : "OFF"}
              </button>
            </div>

            {/* Action buttons */}
            <div className="bg-white px-3 py-2.5 border-b border-gray-200 space-y-2" data-testid="route-controls">
              {!arrivedCurrent ? (
                <>
                  {/* Primary: Start Full Route (multi-stop) */}
                  <button onClick={launchFullRoute} className="w-full py-3.5 bg-[#34A853] hover:bg-[#2D9249] text-white text-base font-bold rounded-lg flex items-center justify-center gap-2 shadow-sm" data-testid="launch-route-btn">
                    <MapPin className="h-5 w-5" />
                    {remainingInSegment > 0 ? `Navigate Route (${remainingInSegment + 1} stops)` : 'Navigate to Stop'}
                  </button>
                  {/* Row: Open Order + Arrived */}
                  <div className="flex gap-2">
                    {curData?.order ? (
                      <button onClick={openOrder} className="flex-1 py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-bold rounded-lg" data-testid="open-current-btn">Open Order</button>
                    ) : (
                      <span className="flex-1 py-2.5 bg-gray-50 text-gray-400 text-sm font-bold rounded-lg border border-gray-200 text-center" data-testid="no-order-label">No Order</span>
                    )}
                    <button onClick={markArrived} className="flex-1 py-2.5 bg-[#6B8F62] hover:bg-[#5A7B52] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1" data-testid="mark-arrived-btn">
                      <CheckCircle className="h-4 w-4" /> Arrived
                    </button>
                  </div>
                  {/* Row: Prev / Navigate single / Next */}
                  <div className="flex gap-2">
                    <button disabled={currentStop <= 0} onClick={() => goToStop(currentStop - 1)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold rounded-lg disabled:opacity-30" data-testid="prev-stop-btn">Prev</button>
                    <button onClick={launchNav} className="flex-1 py-2 bg-white hover:bg-gray-50 text-[#34A853] text-xs font-bold rounded-lg border border-[#34A853]/30 flex items-center justify-center gap-1" data-testid="navigate-single-btn">
                      <MapPin className="h-3 w-3" /> This Stop Only
                    </button>
                    {hasNext ? (
                      <button onClick={() => goToStop(currentStop + 1)} className="flex-1 py-2 bg-[#D1A054] hover:bg-[#B8893E] text-white text-xs font-bold rounded-lg" data-testid="next-stop-btn">Next</button>
                    ) : (
                      <button onClick={endRoute} className="flex-1 py-2 bg-[#C07070] hover:bg-[#A05050] text-white text-xs font-bold rounded-lg" data-testid="end-route-btn">End Route</button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Arrived state */}
                  <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="arrived-confirmation">
                    <p className="text-sm font-bold text-green-700 flex items-center gap-1.5">
                      <CheckCircle className="h-4 w-4" /> Stop {currentStop + 1} Complete
                    </p>
                    {nextData && (
                      <div className="mt-1.5 text-xs text-gray-600">
                        <span className="text-gray-400">Next:</span> <span className="font-semibold text-gray-800">{nextName}</span>
                        {nextAddr && <span className="text-gray-400"> — {nextAddr}</span>}
                      </div>
                    )}
                    {isEndOfSegment && hasNextSegment && (
                      <div className="mt-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-700 font-medium">
                        End of segment {currentSegment + 1}. Tap "Load Next Segment" to continue route.
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {curData?.order && (
                      <button onClick={openOrder} className="flex-1 py-3 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-bold rounded-lg" data-testid="open-current-btn">Open Order</button>
                    )}
                    {hasNext ? (
                      isEndOfSegment && hasNextSegment ? (
                        <button onClick={() => { goToStop(currentStop + 1); }} className="flex-1 py-3 bg-[#D1A054] hover:bg-[#B8893E] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5" data-testid="load-next-segment-btn">
                          <Map className="h-4 w-4" /> Load Next Segment
                        </button>
                      ) : (
                        <button onClick={advanceToNext} className="flex-1 py-3 bg-[#34A853] hover:bg-[#2D9249] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5" data-testid="advance-next-btn">
                          <MapPin className="h-4 w-4" /> Next Stop
                        </button>
                      )
                    ) : (
                      <button onClick={endRoute} className="flex-1 py-3 bg-[#C07070] hover:bg-[#A05050] text-white text-sm font-bold rounded-lg" data-testid="end-route-btn">Finish Route</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        ) : routeStatus === "found" && stops.length > 0 ? (
          <div className="bg-white px-3 py-3 border-b border-gray-200 space-y-2" data-testid="route-controls">
            <button onClick={() => { startRoute(); setTimeout(() => launchRouteSegment(stopsRef.current.slice(0, Math.min(MAX_GMAPS_WAYPOINTS + 1, stopsRef.current.length))), 300); }} className="w-full py-3.5 bg-[#34A853] hover:bg-[#2D9249] text-white font-bold text-sm rounded-lg flex items-center justify-center gap-2 shadow-sm" data-testid="start-route-btn">
              <MapPin className="h-5 w-5" /> Start Route & Navigate ({stops.length} stops)
            </button>
            {/* Route Summary */}
            <div className="grid grid-cols-3 gap-2" data-testid="route-summary">
              <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg px-2 py-1.5 text-center">
                <p className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold">Stops</p>
                <p className="text-sm font-bold text-gray-800" data-testid="summary-stops">{stops.length}</p>
              </div>
              <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg px-2 py-1.5 text-center">
                <p className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold">Miles</p>
                <p className="text-sm font-bold text-gray-800" data-testid="summary-miles">
                  {routingStatus === "loading" ? "…" : routeSummary?.miles != null && routeSummary.miles > 0 ? routeSummary.miles.toFixed(1) : "—"}
                </p>
              </div>
              <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg px-2 py-1.5 text-center">
                <p className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold">Drive Time</p>
                <p className="text-sm font-bold text-gray-800" data-testid="summary-time">
                  {routingStatus === "loading" ? "…" : routeSummary?.minutes != null && routeSummary.minutes > 0 ? (routeSummary.minutes >= 60 ? `${Math.floor(routeSummary.minutes / 60)}h ${Math.round(routeSummary.minutes % 60)}m` : `${Math.round(routeSummary.minutes)} min`) : "—"}
                </p>
              </div>
            </div>
            {routingStatus === "failed" && (
              <div className="px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800 flex items-center gap-1.5" data-testid="routing-warning">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                Could not compute driving route. Using straight-line fallback.
              </div>
            )}
            {routingStatus === "ok" && routeSummary?.skipped > 0 && (
              <div className="px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800 flex items-center gap-1.5" data-testid="routing-skip-warning">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                {routeSummary.skipped} {routeSummary.skipped === 1 ? "address needs" : "addresses need"} correction (skipped).
              </div>
            )}
            {stops.length > MAX_GMAPS_WAYPOINTS + 1 && (
              <p className="text-[10px] text-gray-400 text-center">Route will be divided into {Math.ceil(stops.length / (MAX_GMAPS_WAYPOINTS + 1))} segments for Google Maps</p>
            )}
          </div>
        ) : null}
      </div>

      {/* === MAP === */}
      <div className="flex-1 relative" data-testid="map-container">
        <div ref={mapRef} className="w-full h-full" data-testid="inspector-map" />
        {routeStatus === "none" && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg shadow-sm max-w-[90%]">
            <p className="text-xs text-amber-700 font-medium" data-testid="no-route-message">
              Nenhuma rota disponível para hoje.
            </p>
            <p className="text-[10px] text-amber-600 mt-0.5">
              Contacte o escritório.
            </p>
          </div>
        )}
        {routeStatus === "fetch_failed" && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 bg-[#C07070]/10 border border-[#C07070]/40 rounded-lg shadow-sm max-w-[90%]" data-testid="route-fetch-failed-message">
            <p className="text-xs text-[#8B4545] font-medium">
              Não foi possível carregar a rota.
            </p>
            <p className="text-[10px] text-[#8B4545]/80 mt-0.5">
              Contacte o escritório.
            </p>
          </div>
        )}
        {routeStatus === "empty_gpx" && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg shadow-sm max-w-[90%]" data-testid="route-empty-gpx-message">
            <p className="text-xs text-amber-700 font-medium">
              A rota de hoje está vazia.
            </p>
            <p className="text-[10px] text-amber-600 mt-0.5">
              Contacte o escritório.
            </p>
          </div>
        )}
        {routeStatus === "found" && stops.length > 0 && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-3 py-1 bg-[#A7C4A0]/20 border border-[#A7C4A0]/50 rounded-full shadow-sm" data-testid="route-loaded-message">
            <p className="text-[10px] text-[#4A7A56] font-semibold">
              Route loaded — {stops.length} stop{stops.length === 1 ? "" : "s"}
            </p>
          </div>
        )}
        <div className="absolute top-2 left-2 z-[1000]">
          <button onClick={onBack} className="w-9 h-9 bg-white/95 rounded-full shadow-md flex items-center justify-center" data-testid="map-back-btn">
            <ArrowLeft className="h-4 w-4 text-gray-700" />
          </button>
        </div>
        {routeStatus === "found" && stops.length > 0 && (
          <div className="absolute bottom-2 left-2 z-[1000] flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 border border-gray-200 shadow-sm" data-testid="map-legend">
            <span className="flex items-center gap-1 text-[8px] text-gray-600"><span className="inline-block w-2 h-2 rounded-full bg-[#16A34A]"></span>Start</span>
            <span className="flex items-center gap-1 text-[8px] text-gray-600"><span className="inline-block w-2 h-2 rounded-full bg-[#DC2626]"></span>End</span>
            {routeStarted && <>
              <span className="flex items-center gap-1 text-[8px] text-gray-600"><span className="inline-block w-2 h-2 rounded-full bg-[#D1A054]"></span>Now</span>
              <span className="flex items-center gap-1 text-[8px] text-gray-600"><span className="inline-block w-2 h-2 rounded-full bg-[#6B8F62]"></span>Done</span>
            </>}
          </div>
        )}
      </div>
    </div>
  );
};


export default InspectorMyOrdersPage;
